import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  Box,
  Button,
  IconButton,
  Typography,
  DialogActions,
  Dialog,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import CustomToast from './CustomToast';
import {
  AddOutlined,
  SettingsOutlined,
  CloseOutlined,
  DeleteOutlined,
} from '@mui/icons-material';
import { makeStyles } from '@mui/styles';

import VehicleFormDrawer from './VehicleFormDrawer';
import VehicleSettings from '../Settings/VehicleSettings';
import VehicleTable from './VehicleTable';

import { useVehicleSettings } from '@/hooks/useVehicleSettings';
import { useAllVehicles } from '@/hooks/useAllVehicles';
import { Vehicles, VehicleSettingsResponse } from '@/config/types/Vehicles';
import { LoadClass } from '@/config/types/LoadClass';
import { i18next } from '@/config/i18n';
import { instance } from '@/config/axios';
import { urls } from '@/config/urls';

const useStyles = makeStyles(() => ({
  mainbox: {
    padding: 20,
    display: 'flex',
    flexDirection: 'column',
    height: 'calc(100vh - 57px)',
  },
  maintitle: {
    fontSize: '20px',
    fontWeight: 500,
  },
  settingicon: {
    color: '#ef5927',
    marginRight: 4,
  },
  buttontext: {
    fontSize: 14,
  },
  addIcon: {
    color: 'white',
  },
  buttonclr: {
    color: '#ef5927',
  },
  totalvehicle: {
    fontSize: 14,
    fontWeight: 500,
    textAlign: 'right',
    marginBottom: '10px'
  },
  DeleteTitle: {
    fontSize: '17px',
    marginLeft: '10px'
  },
  deletewrapper: {
    background: '#f59b7d36',
    borderRadius: '50px',
    width: '40px',
    height: '40px'
  },
  DeleteIcon: {
    borderRadius: '50px',
    fontSize: '31px',
    padding: '4px',
    color: '#ef5927',
    background: 'rgb(239 89 39 / 22%)',
    margin: '5px',
    width: '30px',
    height: '30px'
  },
  Deletemessage: {
    color: 'grey',
    margin: '0 50px',
    fontSize: '15px'
  },
  Cancelbtn: {
    border: '1px solid #e5e5e5',
    color: '#000000',
    padding: '5px 16px'
  },
  Deletebtn: {
    background: '#ef5927',
    color: 'white',
    padding: '5px 16px'
  }
}));

type VehicleUpsert = Omit<Vehicles, 'id'> & { notes?: string | null };

const { server } = urls;

const VehicleEditor: React.FC = () => {
  const classes = useStyles();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [search, setSearch] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editData, setEditData] = useState<Vehicles | undefined>(undefined);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [loadClasses, setLoadClasses] = useState<LoadClass[]>([]);
  const [loadingClasses, setLoadingClasses] = useState(false);
  const [toast, setToast] = useState<{
    open: boolean;
    severity: 'success' | 'error' | 'info' | 'warning';
    msg: string;
  }>({ open: false, severity: 'success', msg: '' });
  const {
    data: settings,
    loading: settingsLoading,
    error: settingsError,
    refetch: refetchSettings,
  } = useVehicleSettings(settingsOpen);

  const {
    data: allVehicles,
    loading: allLoading,
    error: allError,
    refetch: refetchAll
  } = useAllVehicles();

  // filter by search query
  const filtered = useMemo(() => {
    if (!search) return allVehicles;
    const q = search.trim().toLowerCase();
    return allVehicles.filter(v =>
      v.name.toLowerCase().includes(q)
    );
  }, [allVehicles, search]);

  const totalCount = filtered.length;

  const handleSaveSettings = useCallback(
    async (updated: VehicleSettingsResponse) => {
      try {
        await instance.patch(server.settings.vehicle, updated);
        setToast({
          open: true,
          severity: 'success',
          msg: i18next.t('common:toast.saveSuccess'),
        });
        refetchSettings();
        setSettingsOpen(false);
      } catch (err: any) {
        setToast({
          open: true,
          severity: 'error',
          msg: err?.response?.data?.detail || err.message || 'Failed to save settings.',
        });
      }
    },
    [refetchSettings]
  );

  const handleSave = useCallback(
    async (form: VehicleUpsert, id?: string) => {
      if (id) {
        await instance.patch(`${server.vehicle}${id}`, form);
      } else {
        await instance.post(server.vehicle, [form]);
      }
      await refetchAll();
      setDrawerOpen(false);
    },
    [refetchAll]
  );

  const handleDelete = useCallback(async (id: string) => {
    try {
      await instance.delete(`${server.vehicle}${id}`);
      setToast({ open: true, severity: 'success', msg: i18next.t('common:toast.deleteSuccess') });
      refetchAll();
      setConfirmDeleteId(null);
    } catch (err: any) {
      setToast({
        open: true,
        severity: 'error',
        msg: err?.response?.data?.detail || err.message || 'Failed to delete vehicle.',
      });
    }
  }, [refetchAll]);

  const handleToggleActive = useCallback(
    async (vehicle: Vehicles, isActive: boolean) => {
      try {
        await instance.patch(`${server.vehicle}${vehicle.id}`, { is_active: isActive });

        setToast({
          open: true,
          severity: 'success',
          msg: i18next.t(
            isActive ? 'common:toast.ActiveMessage' : 'common:toast.InactiveMessage',
            { name: vehicle.name || i18next.t('common:vehicleEditor.vehicle') }
          ),
        });

        refetchAll();
      } catch (err: any) {
        setToast({
          open: true,
          severity: 'error',
          msg: err?.response?.data?.detail || err.message || 'Something went wrong.',
        });
      }
    },
    [refetchAll]
  );

  useEffect(() => {
    const fetch = async () => {
      setLoadingClasses(true);
      try {
        const { data } = await instance.get<LoadClass[]>(server.loadClasses);
        setLoadClasses(data);
      } finally {
        setLoadingClasses(false);
      }
    };
    fetch();
  }, []);

  return (
    <Box className={classes.mainbox} data-testid="vehicle-editor">
      {/* Header */}
      <Box display="flex" justifyContent="space-between" mb={2}>
        <Typography className={classes.maintitle}>
          {i18next.t('common:vehicleEditor.vehiclesTitle')}
        </Typography>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            onClick={() => setSettingsOpen(true)}
            sx={{ textTransform: 'none' }}
            data-testid="open-vehicle-settings-btn"
          >
            <SettingsOutlined className={classes.settingicon} />
            <Typography className={classes.buttontext}>
              {i18next.t('common:vehicleSetting.vehiclesetting')}
            </Typography>
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setEditData(undefined);
              setDrawerOpen(true);
            }}
            sx={{ textTransform: 'none' }}
            data-testid="add-vehicle-btn"
          >
            <AddOutlined className={classes.addIcon} />
            <Typography className={classes.buttontext}>
              {i18next.t('common:vehicleEditor.Addvehicle')}
            </Typography>
          </Button>
        </Box>
      </Box>

      {/* Total count */}
      <Typography
        align="right"
        mb={1}
        variant="subtitle2"
        className={classes.totalvehicle}
        data-testid="total-vehicle-count"
      >
        {i18next.t('common:vehicleTable.totalvehicle')}: {totalCount}
      </Typography>

      {/* Table fills remaining space */}
      <Box
        data-testid="vehicle-table-container"
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          width: '100%',
        }} >
        <VehicleTable
          vehicles={allVehicles ?? []}
          rowCount={totalCount}
          loading={allLoading}
          error={allError ?? undefined}
          page={page}
          pageSize={pageSize}
          onPageChange={(p) => setPage(p)}
          onPageSizeChange={(s) => {
            setPageSize(s);
            setPage(1);
          }}
          visibleCols={{}}
          onColumnVisibilityChange={() => { }}
          onEdit={(v) => {
            setEditData(v);
            setDrawerOpen(true);
          }}
          onDelete={(id) => setConfirmDeleteId(id)}
          onToggleActive={handleToggleActive}
        />
      </Box>
      {/* Delete confirmation dialog */}
      <Dialog
        open={!!confirmDeleteId}
        onClose={() => setConfirmDeleteId(null)}
        aria-labelledby="delete-shipment-dialog-title"
        data-testid="delete-vehicle-dialog"
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', pr: 1, pb: 0 }}>
          <div className={classes.deletewrapper}>
            <DeleteOutlined className={classes.DeleteIcon} sx={{ mr: 2 }} />
          </div>
          <Typography className={classes.DeleteTitle} variant="subtitle2" component="span" id="delete-shipment-dialog-title">
            {i18next.t('common:vehicleEditor.deleteTitle')}
          </Typography>
          <IconButton
            aria-label="close"
            onClick={() => setConfirmDeleteId(null)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
            data-testid="delete-dialog-close"
          >
            <CloseOutlined />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <Typography className={classes.Deletemessage}>
            {i18next.t('common:vehicleEditor.deleteConfirm')}
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button
            className={classes.Cancelbtn}
            onClick={() => setConfirmDeleteId(null)}
            data-testid="delete-dialog-cancel"
          >
            {i18next.t('common:buttons.cancel')}
          </Button>
          <Button
            className={classes.Deletebtn}
            onClick={() => handleDelete(confirmDeleteId!)}
            data-testid="delete-dialog-confirm">
            {i18next.t('common:buttons.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Drawers & Settings */}
      <VehicleFormDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        initialData={editData}
        onSave={handleSave}
        loadClasses={loadClasses}
        loadingClasses={loadingClasses}
      />

      <VehicleSettings
        open={settingsOpen}
        settings={settings}
        loading={settingsLoading}
        error={settingsError ?? undefined}
        onClose={() => setSettingsOpen(false)}
        onSave={handleSaveSettings}
        loadClasses={loadClasses}
        loadingClasses={loadingClasses}
      />
      <CustomToast
        open={toast.open}
        message={toast.msg}
        severity={toast.severity}
        autoHideMs={2500}
        anchor={{ vertical: 'bottom', horizontal: 'center' }}
        onClose={() => setToast(t => ({ ...t, open: false }))}
      />
    </Box>
  );
};

export default VehicleEditor;
